import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatPurchaseOrderDate, formatPurchaseOrderDateTime } from '../utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatBuyerNames,
    formatCurrency,
    formatDeliveryType,
    formatTemperatureRange,
    normalizeOptionalText,
} from '../utils/purchaseOrderDisplayUtils';
import { Badge } from '~/shadcn/components/ui/Badge';

export function PurchaseOrderPrimaryInfoCard({ purchaseOrder }) {
    const resolvedPurchaseOrder = purchaseOrder ?? null;

    const {
        buyers,
        createdAt,
        crossDockDeliveryDate,
        crossDockName,
        deliveryType,
        department,
        estimatedFreightCost,
        facilityName,
        id,
        maxTempReq,
        minTempReq,
        pickupDate,
        poTotalCost,
        produceOrGrocery,
        scheduledDeliveryDate,
        sentToVendor,
        shipperID,
        shipperName,
        specialInstructions,
        status,
        truckID,
        vendorName,
    } = resolvedPurchaseOrder ?? {};

    const infoSections = useMemo(
        () => [
            {
                label: 'PO #',
                value: normalizeOptionalText(id),
            },
            {
                label: 'Status',
                value: normalizeOptionalText(status),
            },
            {
                label: 'Created On',
                value: formatPurchaseOrderDateTime(createdAt, FALLBACK_TEXT),
            },
            {
                label: 'Facility',
                value: normalizeOptionalText(facilityName),
            },
            {
                label: 'Buyer(s)',
                value: formatBuyerNames(buyers),
            },
            {
                label: 'Produce/Grocery',
                value: normalizeOptionalText(produceOrGrocery),
            },
            {
                label: 'Delivery Type',
                value: formatDeliveryType(deliveryType),
                render: (value) => (
                    <Badge variant="secondary" className="uppercase tracking-wide justify-start">
                        {value}
                    </Badge>
                ),
            },
            {
                label: 'Vendor',
                value: normalizeOptionalText(vendorName),
            },
            {
                label: 'Shipper',
                value: normalizeOptionalText(
                    shipperName === 'Unknown' || shipperName === 'unknown' ? null : shipperName,
                ),
            },
            {
                label: 'Pick Up Date',
                value: formatPurchaseOrderDate(pickupDate, FALLBACK_TEXT),
            },
            {
                label: 'Scheduled Delivery',
                value: formatPurchaseOrderDateTime(scheduledDeliveryDate, FALLBACK_TEXT),
            },
            {
                label: 'Temperature (Min - Max)',
                value: formatTemperatureRange(minTempReq, maxTempReq),
            },
            {
                label: 'Cross Dock',
                value: normalizeOptionalText(crossDockName),
            },
            {
                label: 'Cross Dock Delivery Date',
                value: formatPurchaseOrderDate(crossDockDeliveryDate, FALLBACK_TEXT),
            },
            {
                label: 'Truck ID',
                value: normalizeOptionalText(truckID),
            },
            {
                label: 'Freight Cost',
                value: formatCurrency(estimatedFreightCost),
            },
            {
                label: 'Total Cost',
                value: formatCurrency(poTotalCost),
            },
            {
                label: 'Special Instructions',
                value: normalizeOptionalText(specialInstructions),
            },
        ],
        [
            buyers,
            createdAt,
            crossDockDeliveryDate,
            crossDockName,
            deliveryType,
            department,
            estimatedFreightCost,
            facilityName,
            id,
            maxTempReq,
            minTempReq,
            pickupDate,
            poTotalCost,
            produceOrGrocery,
            scheduledDeliveryDate,
            sentToVendor,
            shipperID,
            shipperName,
            specialInstructions,
            status,
            truckID,
            vendorName,
        ],
    );

    return (
        <div className="rounded-md border bg-card p-6 shadow-sm">
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-1">
                    <h2 className="text-left text-lg font-semibold tracking-tight text-foreground">
                        Purchase Order Information
                    </h2>
                </div>

                <dl className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
                    {infoSections.map(({ label, render, value }) => (
                        <div key={label} className="flex flex-col gap-1">
                            <dt className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                {label}
                            </dt>
                            <dd className="text-left text-base font-medium text-foreground">
                                {value !== FALLBACK_TEXT && typeof render === 'function' ? render(value) : value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </div>
    );
}

PurchaseOrderPrimaryInfoCard.propTypes = {
    purchaseOrder: PropTypes.object,
};

PurchaseOrderPrimaryInfoCard.defaultProps = {
    purchaseOrder: null,
};

PurchaseOrderPrimaryInfoCard.displayName = 'PurchaseOrderPrimaryInfoCard';
